<template>
  <div
    class="v-map b-player_map js-player_map"
    id="js-player_map"
  ></div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import ymaps from 'ymaps';

import { PointMoveEvent } from '@/classes/widgets/PointMoveEvent';

const POINT_PRESETS = {
  DEFAULT: 'islands#blackIcon',
  DEFAULT_DOTTED: 'islands#blackDotIcon',
  ACTIVE: 'islands#darkBlueIcon',
  ACTIVE_DOTTED: 'islands#darkBlueDotIcon',
}

export default {
  name: 'Map',

  props: {
    maxZoom: Number,
    maxResolvedZoom: Number,
    width: Number,
    height: Number,
    tilesPath: String,
    tilesExtension: String,
    points: Array,
    activePointId: Number,
  },
  data: () => ({
    mapInstance: null,
    promise: null,
    uid: null,
  }),
  computed: {
    windowMapPlayerId() {
      return `map-player-${ this.uid }`;
    },
  },
  watch: {
    points: {
      deep: true,
      handler() {
        const activePoint = this.getActivePoint();

        if (!activePoint) {
          return;
        }

        const activePointOnMap = this.getActivePointOnMap();
        this.setPointActivePreset(activePointOnMap);
      },
    },
  },

  created() {
    this.uid = getCurrentInstance().uid;
  },
  async mounted() {
    await this.initPackage();
    this.initMap();
    this.initPoints();
    await this.initScale();
  },

  methods: {
    async initPackage() {
      window.ymapsInstance = await ymaps.load();
    },
    initMap() {
      const LAYER_NAME = 'user#layer',
        MAP_TYPE_NAME = 'user#customMap',
        // PANORAMA_DATA = getPanoramaData(),

        /* Для того чтобы вычислить координаты левого нижнего и правого верхнего углов прямоугольной координатной
         * области, нам необходимо знать максимальный зум, ширину и высоту изображения в пикселях на максимальном зуме.
         */
        MAX_ZOOM = this.maxZoom,
        PIC_WIDTH = this.width,
        PIC_HEIGHT = this.height;

      /**
       * Конструктор, создающий собственный слой.
       */
      const Layer = () => {
        const layer = new window.ymapsInstance.Layer(this.tilesPath + '/%z/%x-%y.' + this.tilesExtension);

        // Указываем доступный диапазон масштабов для данного слоя.
        layer.getZoomRange = () => window.ymapsInstance.vow.resolve([0, this.maxResolvedZoom]);

        // Добавляем свои копирайты.
        layer.getCopyrights = () => window.ymapsInstance.vow.resolve('©');

        return layer;
      };

      // Добавляем в хранилище слоев свой конструктор.
      window.ymapsInstance.layer.storage.add(LAYER_NAME, Layer);

      /**
       * Создадим новый тип карты.
       * MAP_TYPE_NAME - имя нового типа.
       * LAYER_NAME - ключ в хранилище слоев или функция конструктор.
       */
      const mapType = new window.ymapsInstance.MapType(MAP_TYPE_NAME, [LAYER_NAME]);

      // Сохраняем тип в хранилище типов.
      window.ymapsInstance.mapType.storage.add(MAP_TYPE_NAME, mapType);

      // Вычисляем размер всех тайлов на максимальном зуме.
      const worldSize = Math.pow(2, MAX_ZOOM) * 256;

      /**
       * Создаем карту, указав свой новый тип карты.
       */
      window[this.windowMapPlayerId] = new window.ymapsInstance.Map('js-player_map', {
        center: [0, 0],
        zoom: 1,
        controls: ['zoomControl'],
        type: MAP_TYPE_NAME
      }, {
        // Задаем в качестве проекции Декартову. При данном расчёте центр изображения будет лежать в координатах [0, 0].
        projection: new window.ymapsInstance.projection.Cartesian(
          [
            [PIC_HEIGHT / 2 - worldSize, -PIC_WIDTH / 2],
            [PIC_HEIGHT / 2, worldSize - PIC_WIDTH / 2]
          ],
          [false, false]
        ),
        // Устанавливаем область просмотра карты так, чтобы пользователь не смог выйти за пределы изображения.
        restrictMapArea: [
          [-PIC_HEIGHT / 2, -PIC_WIDTH / 2],
          [PIC_HEIGHT / 2, PIC_WIDTH / 2]
        ],
      });

      window[this.windowMapPlayerId].container.fitToViewport();
    },
    initPoints() {
      // 1 отобразим точки на плане
      for (let pointData of this.points) {
        const point = new window.ymapsInstance.Placemark(
          [
            pointData.x,
            pointData.y
          ],
          {
            id: pointData.id,
            oldNew: 'old',
            params: pointData,
          },
          {
            preset: pointData.dotted
              ? POINT_PRESETS.DEFAULT_DOTTED
              : POINT_PRESETS.DEFAULT,
            // перемещение метки (если параметр включен у точки)
            draggable: pointData.draggable || this.$route.query.movable !== undefined,
            // картинка иконки
            iconImageHref: 'http://api.yandex.ru/maps/doc/jsapi/2.x/examples/images/myIcon.gif',
            // размеры картинки
            iconImageSize: [30, 42],
            // смещение картинки
            iconImageOffset: [-3, -42],
          },
        );

        point.events.add(['click'], event => {
          this.pointClickHandler(event.get('target'));
        });
        point.events.add(['dragend'], event => {
          this.geometryChangeHandler(event.get('target'));
        });

        window[this.windowMapPlayerId].geoObjects.add(point);

        // добавим эту точку в глобальный массив точек на плане
        // window.planPoints.push(point);
      }

      // убираем нативное поведение по двойному клику
      window[this.windowMapPlayerId].events.add('dblclick', function (e) {
        // при двойном клике увеличение карты не произойдет
        e.preventDefault();
      });
    },

    pointClickHandler(newChosenPoint) {
      const clickedPointId = newChosenPoint.properties._data.id;

      // если уже существовала выбранная точка
      if (this.activePointId) {
        // если прошлая выбранная точка - та же самая, снимем с неё выделение
        if (clickedPointId === this.activePointId) {
          this.setPointInactivePreset(newChosenPoint);
          this.$emit('changeActivePoint', null);
        }
        // если прошлая выбранная точка другая
        else {
          const lastClickedPoint = this.getActivePointOnMap();

          // уберем выделение с прошлой выделенной точки
          this.setPointInactivePreset(lastClickedPoint);
          this.setPointActive(newChosenPoint);
        }

        return;
      }

      this.setPointActive(newChosenPoint);
    },
    getActivePointOnMap() {
      let activePoint;

      // TODO: поменять на find
      window[this.windowMapPlayerId].geoObjects.each((geoObject) => {
        if (geoObject.properties.get('id') === this.activePointId) {
          activePoint = geoObject;
        }
      })

      return activePoint;
    },
    getActivePoint() {
      return this.findPoint(this.activePointId);
    },

    geometryChangeHandler(eventTarget) {
      this.$emit('pointMoved', new PointMoveEvent(
        eventTarget.properties._data.id,
        Math.round(eventTarget.geometry._coordinates[0]),
        Math.round(eventTarget.geometry._coordinates[1]),
      ));
    },

    findPoint(pointId) {
      return this.points.find(loopPoint => loopPoint.id === pointId);
    },

    setPointActive(pointOnMap) {
      this.setPointActivePreset(pointOnMap);
      this.$emit('changeActivePoint', pointOnMap.properties.get('id'));
    },
    setPointActivePreset(pointOnMap) {
      const point = this.findPoint(pointOnMap.properties._data.id);

      pointOnMap.options.set('preset', point.dotted
        ? POINT_PRESETS.ACTIVE_DOTTED
        : POINT_PRESETS.ACTIVE);
    },
    setPointInactivePreset(pointOnMap) {
      const point = this.findPoint(pointOnMap.properties._data.id);

      pointOnMap.options.set('preset', point.dotted
        ? POINT_PRESETS.DEFAULT_DOTTED
        : POINT_PRESETS.DEFAULT);
    },

    async initScale() {
      await this.$nextTick();
    },
  },
}
</script>

<style scoped>
.v-map {
  box-sizing: content-box;
}
</style>
